import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as _ from 'lodash';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useUserStore } from '../stores/UserStore';
import { MESSAGE_TYPE } from '../types/chatTypings';
import { DateService } from './DateService';
import { decrypt } from './EncryptionService';
import { RestService } from './RestService';
import { extendFormData, MULTIPART_HEADERS } from './Utils';

export const fetchChats = ({ pageParam }) => {
  return RestService.get(`/chats/messages?page=${pageParam}`);
};

export const useChatsData = () => {
  return useInfiniteQueryData([QUERY_KEYS.CHATS], fetchChats);
};

export const fetchMessages = (queryKey, pageParam, isGroup) => {
  return RestService.get(`/chats?frndUuid=${queryKey[1]}&isGroup=${isGroup}&page=${pageParam}`);
};

export const useMessagesData = (uuid: string, isGroup: boolean = false) => {
  return useInfiniteQueryData([QUERY_KEYS.MESSAGES, uuid], ({ queryKey, pageParam }) => fetchMessages(queryKey, pageParam, isGroup));
};

export const onSuccessAddMessageData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) {
    await queryClient.invalidateQueries({ queryKey });
    return;
  }
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any, index: number) => {
        return {
          ...pageData,
          data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
        };
      })
    };
  });
  return { oldData };
};

export const onAddMessageMutationSuccess = async (queryClient, newData) => {
  const queryKey = [QUERY_KEYS.MESSAGES, newData.data.frndUuid];
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) {
    await queryClient.invalidateQueries({ queryKey });
    return;
  }
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any, index: number) => {
        return {
          ...pageData,
          data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
        };
      })
    };
  });
};

export const useAddMessageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      extendFormData(formData, { userUuid, businessUuid });
      return RestService.post('/chats', formData, MULTIPART_HEADERS);
    },
    onSuccess: async (newData: any) => onAddMessageMutationSuccess(queryClient, newData)
  });
};

export const useDeleteMessagesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ frndUuid, isGroup = false }) => RestService.delete(`/chats/remove/${frndUuid}/?isGroup=${isGroup}`),
    onSuccess: async (newData: any, data: any) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.CHATS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGES, data.frndUuid] });
      const oldDataChats = queryClient.getQueryData([QUERY_KEYS.CHATS]);
      if (!oldDataChats) return;
      queryClient.setQueryData([QUERY_KEYS.CHATS], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.filter(item => item.chatWith !== data.frndUuid)
            };
          })
        };
      });
    }
  });
};

export const updateChatsUserStatus = async (queryClient: any, queryKey: any, data: any) => {
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData && !!data) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.map(item => {
              if (item.frndUuid === data.uuid) {
                return {
                  ...item,
                  isOnline: data.isOnline,
                  lastActive: DateService.getCurrentDate()
                };
              }
              return item;
            })
          };
        })
      };
    });
  }
  const oldData1 = queryClient.getQueryData([QUERY_KEYS.USERS_ALL]);
  if (!!oldData1 && data) {
    queryClient.setQueryData([QUERY_KEYS.USERS_ALL], (oldQueryData: any) => {
      return {
        ...oldQueryData,
        data: oldQueryData.data.map(item => {
          if (item.uuid === data.uuid) {
            return {
              ...item,
              isOnline: data.isOnline,
              updated: DateService.getCurrentDate()
            };
          }
          return item;
        })
      };
    });
  }
};

export const addNewChatsUser = async (queryClient: any, queryKey: any, data: any) => {
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData || !data) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any, index: number) => {
        const filteredItems = pageData.data.filter(item => item.chatWith !== data.chatWith);
        return {
          ...pageData,
          data: index === 0 ? [data, ...filteredItems] : filteredItems
        };
      })
    };
  });
};

export const updateNewChatsMessage = async (queryClient: any, queryKey: any, data: any) => {
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) {
    await queryClient.invalidateQueries({ queryKey });
    return;
  }
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any, pageIndex: number) => {
        return {
          ...pageData,
          data: pageData.data.map(item => (item.chatWith === data.frndUuid ? { ...item, ...data } : item))
        };
      })
    };
  });
};

export const getChatMessageText = (user: any) => {
  if (!!user.message) {
    return decrypt(user.message);
  } else if (user.type === MESSAGE_TYPE.IMAGE) {
    return 'Image';
  } else if (user.type === MESSAGE_TYPE.GIF) {
    return 'Video';
  } else if (user.type === MESSAGE_TYPE.AUDIO) {
    return 'Audio call';
  } else if (user.type === MESSAGE_TYPE.VIDEO) {
    return 'Video call';
  } else if (user.type === MESSAGE_TYPE.FILE) {
    return 'File';
  } else {
    return 'New message';
  }
};

export const useReactMessageMutation = () => {
  const queryClient = useQueryClient();
  const myUuid = useUserStore.getState().UUID;
  return useMutation({
    mutationFn: ({ message, reaction, frndUuid }: any) => RestService.update('/chats/react/' + message.uuid, reaction),
    onMutate: async ({ message, reaction, frndUuid }: any) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.MESSAGES, frndUuid] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.MESSAGES, frndUuid]);
      if (!oldData) return;
      queryClient.setQueryData([QUERY_KEYS.MESSAGES, frndUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.map(item => {
                if (item.uuid === message.uuid) {
                  const reactions = _.filter(message.reactions, r => r.uuid !== myUuid) || [];
                  return { ...item, reactions: [...reactions, reaction] };
                }
                return item;
              })
            };
          })
        };
      });
      return { oldData };
    }
  });
};

export const useUpdateMessageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: any) => RestService.update('/chats/' + message.uuid, message),
    onSuccess: async (newData, message: any) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.MESSAGES, message.frndUuid] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.MESSAGES, message.frndUuid]);
      if (!oldData) return;
      queryClient.setQueryData([QUERY_KEYS.MESSAGES, message.frndUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.map(item => (item.uuid === message.uuid ? newData.data : item))
            };
          })
        };
      });
      return { oldData };
    }
  });
};

export const useDeleteMessageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: any) => RestService.delete('/chats/' + message.uuid),
    onMutate: async (message: any) => {
      const userUuid = useUserStore.getState().UUID;
      const frndUuid = message.userUuid === userUuid || message.isGroup ? message.frndUuid : message.userUuid;
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.MESSAGES, frndUuid] });
      const oldData: any = queryClient.getQueryData([QUERY_KEYS.MESSAGES, frndUuid]);
      if (!oldData) return;
      queryClient.setQueryData([QUERY_KEYS.MESSAGES, frndUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.filter(item => item.uuid !== message.uuid)
            };
          })
        };
      });
      return { oldData };
    }
  });
};

export const isChatSeen = chat => {
  const userUuid = useUserStore.getState().UUID;
  return !!chat.seen.includes(userUuid);
};

export const onChatSeen = (queryClient, data) => {
  const queryKey = [QUERY_KEYS.CHATS];
  const userUuid = useUserStore.getState().UUID;
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData && !!data) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.map(item => {
              if (item.frndUuid === data.frndUuid) {
                return {
                  ...item,
                  seen: [userUuid, ...item.seen]
                };
              }
              return item;
            })
          };
        })
      };
    });
  }
};
